import Button from "components/ui/button/Button";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { useAllCpvCodes } from "lib/hooks/api/cpv_codes/useAllCpvCodes";
import { SignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { buildSignalSettingsExportRows, exportSignalSettingsToCsv } from "./signalSettingsExport";

type ExportSignalSettingsButtonProps = {
  // The saved settings, so the file always matches what the team's feed uses
  savedSignals: SignalSettings | undefined;
  hasUnsavedChanges: boolean;
};

export default function ExportSignalSettingsButton({
  savedSignals,
  hasUnsavedChanges,
}: ExportSignalSettingsButtonProps) {
  const { logEvent } = useTracking();
  const hasCpvCodes = (savedSignals?.cpvCodes?.length ?? 0) > 0;
  // Only wait for CPV names when there are codes to describe; on failure we export codes alone
  const { data: cpvData, isLoading: isLoadingCpvCodes } = useAllCpvCodes({ enabled: hasCpvCodes });

  const rows = savedSignals ? buildSignalSettingsExportRows(savedSignals, cpvData?.cpvCodes) : [];

  let disabledReason: string | undefined;
  if (hasUnsavedChanges) {
    disabledReason = "Save or discard your changes to export";
  } else if (rows.length === 0) {
    disabledReason = "Add signals to export them";
  }

  const handleExport = () => {
    exportSignalSettingsToCsv(rows);
    logEvent(EventNames.exportedData, {
      "Action type": "Button clicked",
      "Data type": EventDataTypes.signalSettings,
      Format: "csv",
      Count: rows.length,
    });
  };

  const button = (
    <Button
      buttonType="secondary"
      icon="download01"
      onClick={handleExport}
      disabled={!!disabledReason}
      loading={hasCpvCodes && isLoadingCpvCodes}
    >
      Export
    </Button>
  );

  if (!disabledReason) {
    return button;
  }

  return (
    <TooltipComponent content={disabledReason}>
      {/* Disabled buttons swallow pointer and focus events, so the span carries the tooltip */}
      <span tabIndex={0}>{button}</span>
    </TooltipComponent>
  );
}
